import { useCallback, useEffect, useState } from 'react';
import {
  fetchTemplateStats,
  toggleTemplateLike as toggleTemplateLikeApi,
  type TemplateStat,
} from '../api/templates';
import { useAuth } from '../context/AuthContext';

const emptyStat = (): TemplateStat => ({ usageCount: 0, likeCount: 0 });

export const useTemplateStats = () => {
  const { user, openLogin } = useAuth();
  const [stats, setStats] = useState<Record<string, TemplateStat>>({});
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [likingId, setLikingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetchTemplateStats()
      .then((data) => {
        if (cancelled) {
          return;
        }
        setStats(data.stats);
        setLikedIds(new Set(data.likedTemplateIds));
      })
      .catch(() => {
        if (!cancelled) {
          setStats({});
          setLikedIds(new Set());
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const getStat = useCallback(
    (templateId: string): TemplateStat => stats[templateId] ?? emptyStat(),
    [stats],
  );

  const isLiked = useCallback((templateId: string): boolean => likedIds.has(templateId), [likedIds]);

  const toggleLike = useCallback(
    async (templateId: string, event?: React.MouseEvent) => {
      event?.stopPropagation();
      event?.preventDefault();

      if (!user) {
        openLogin();
        return;
      }
      if (likingId) {
        return;
      }

      const previousLiked = likedIds.has(templateId);
      const previousCount = stats[templateId]?.likeCount ?? 0;
      const optimisticLiked = !previousLiked;
      const optimisticCount = Math.max(0, previousCount + (optimisticLiked ? 1 : -1));

      setLikingId(templateId);
      setLikedIds((current) => {
        const next = new Set(current);
        if (optimisticLiked) {
          next.add(templateId);
        } else {
          next.delete(templateId);
        }
        return next;
      });
      setStats((current) => ({
        ...current,
        [templateId]: {
          ...(current[templateId] ?? emptyStat()),
          likeCount: optimisticCount,
        },
      }));

      try {
        const result = await toggleTemplateLikeApi(templateId);
        setLikedIds((current) => {
          const next = new Set(current);
          if (result.liked) {
            next.add(templateId);
          } else {
            next.delete(templateId);
          }
          return next;
        });
        setStats((current) => ({
          ...current,
          [templateId]: {
            ...(current[templateId] ?? emptyStat()),
            likeCount: result.likeCount,
          },
        }));
      } catch {
        setLikedIds((current) => {
          const next = new Set(current);
          if (previousLiked) {
            next.add(templateId);
          } else {
            next.delete(templateId);
          }
          return next;
        });
        setStats((current) => ({
          ...current,
          [templateId]: {
            ...(current[templateId] ?? emptyStat()),
            likeCount: previousCount,
          },
        }));
      } finally {
        setLikingId(null);
      }
    },
    [likedIds, likingId, openLogin, stats, user],
  );

  const bumpUsage = useCallback((templateId: string) => {
    setStats((current) => ({
      ...current,
      [templateId]: {
        ...(current[templateId] ?? emptyStat()),
        usageCount: (current[templateId]?.usageCount ?? 0) + 1,
      },
    }));
  }, []);

  return {
    getStat,
    isLiked,
    toggleLike,
    bumpUsage,
    likingId,
  };
};
